import { ref, computed, watch, onMounted } from 'vue'
import type { UserShape } from './types'
import { GUEST_USER } from './types'

export function getAuthToken(): string | null {
  const raw = document.cookie
    .split('; ')
    .find(row => row.startsWith('wad-auth._token.local='))
    ?.split('=').slice(1).join('=')
  if (!raw || raw === 'false') return null
  return raw.startsWith('Bearer') ? raw : `Bearer ${raw}`
}

function getMenuUserCookie(): Pick<UserShape, 'account_type' | 'image_url'> | null {
  const raw = document.cookie
    .split('; ')
    .find(row => row.startsWith('wad-menu-user='))
    ?.split('=')
    .slice(1)
    .join('=') // preserve any = chars in the JSON value
  if (!raw) return null
  try {
    return JSON.parse(decodeURIComponent(raw))
  } catch {
    return null
  }
}

function clearAuthCookies(): void {
  const parts = location.hostname.split('.')
  const domain = '.' + (parts.length > 1 ? parts.slice(-2).join('.') : location.hostname)
  document.cookie = `wad-menu-user=; Max-Age=0; path=/; domain=${domain}`
  document.cookie = `wad-auth._token.local=; Max-Age=0; path=/; domain=${domain}`
}

export function useMenuAuth(opts: {
  apiBaseUrl: string
  userProp: () => UserShape | undefined
}) {
  async function revokeSession(): Promise<void> {
    const token = getAuthToken()
    if (!token) return
    try {
      await fetch(`${opts.apiBaseUrl}/v2/user/logout`, {
        method: 'POST',
        headers: { Authorization: token, Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'include',
      })
    } catch { /* proceed with logout regardless */ }
  }
  const internalUser = ref<UserShape>({ ...GUEST_USER })
  // Suppress the guest icon while a fetch is in flight (no-cookie fallback)
  const isAuthPending = ref(false)
  // True only once full user data is available — used to gate things like notification badges
  const isUserReady = ref(false)

  onMounted(async () => {
    if (opts.userProp()) {
      isUserReady.value = true
      return
    }

    const token = getAuthToken()
    const menuCookie = getMenuUserCookie()

    if (!token) {
      isUserReady.value = true
      return
    }

    if (menuCookie) {
      internalUser.value = { ...GUEST_USER, ...menuCookie }
    }

    // Cookie absent but token present: suppress guest icon while fetching
    if (!menuCookie) isAuthPending.value = true

    try {
      const res = await fetch(`${opts.apiBaseUrl}/v2/user`, {
        headers: { Authorization: token, Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (res.ok) {
        const data = await res.json()
        internalUser.value = data.data ?? { ...GUEST_USER }
      }
    } catch { /* silent */ }
    finally {
      isAuthPending.value = false
      isUserReady.value = true
    }
  })

  const lastKnownUserProp = ref<UserShape | undefined>(undefined)

  watch(() => opts.userProp(), (val) => {
    if (val !== undefined) lastKnownUserProp.value = val
  }, { immediate: true })

  const resolvedUser = computed<UserShape>(() => lastKnownUserProp.value ?? internalUser.value)

  return { resolvedUser, isAuthPending, isUserReady, revokeSession, clearAuthCookies }
}
